<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>

    <b-card>
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h4 class="font-weight-bold">集點卡管理</h4>
        <div class="d-flex align-items-center" style="width: 60%">
          <AppSingleSelect
            class="mr-1"
            v-model="selected_branch"
            :options="branchOptions"
            :disabled="disabled_branch_select"
            @input="branchChange"
          ></AppSingleSelect>
          <b-button
            v-if="checkPermission([consts.REWARD_CARD_CREATE])"
            class="flex-shrink-0 mr-2"
            variant="primary"
            :to="{ name: 'RewardCardCreate' }"
            ><i class="fa fa-plus"></i>新增集點卡</b-button
          >
          <b-input-group class="ml-0 ml-xl-4">
            <b-form-input
              v-model="query.keyword"
              v-on:keyup.enter="handlePageReset"
              placeholder="請輸入集點卡名稱"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageReset">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <b-card-text>
        <b-overlay :show="showLoading" rounded="sm">
          <b-table
            striped
            hover
            responsive
            :items="rewardCards"
            :fields="fields"
            @sort-changed="handleSort"
          >
            <template #cell(start_end_time)="data">
              <div>
                {{ dateFormatter(data.item.start_at) }}
              </div>
              <div>
                {{ dateFormatter(data.item.end_at) }}
              </div>
            </template>
            <template #cell(branch_id)="data">
              <div>{{ getBranchName(data.item.branch_id) }}</div>
            </template>
            <template #cell(actions)="data">
              <copy-url-button
                class="mr-1"
                v-if="checkPermission([consts.REWARD_CARD_LIST_VIEW])"
                :url="generateLiffUrl(`rewards-card/${data.item.id}`)"
                :title="data.item.title"
              >
                <template #qrcode>
                  <div class="d-flex flex-column align-items-center">
                    <vue-qr
                      :text="generateLiffUrl(`rewards-card/${data.item.id}`)"
                      ref="qrcode"
                    ></vue-qr>
                  </div>
                </template>
                <template #qrcode-download-button>
                  <b-button
                    variant="primary"
                    @click="downloadQRCode(data.item.title)"
                  >
                    下載QRCode
                  </b-button>
                </template>
              </copy-url-button>
              <b-dropdown
                class="mr-1"
                variant="secondary"
                v-if="checkPermission([consts.POINT_LIST_VIEW])"
              >
                <template slot="button-content">
                  <i class="mdi mdi-menu"></i>
                </template>
                <b-dropdown-item
                  :to="{
                  name:'PointLogList',
                   query: {
                    merchant_id: data.item.merchant_id,
                    event_id: data.item.event_id,
                    sort_by: 'created_at',
                    order_by: 'desc'
                  }
              }"
                >
                  集點與兌換紀錄
                </b-dropdown-item>
                <b-dropdown-item
                  :to="{
                  name:'PersonalPoint',
                  query: {
                    merchant_id: data.item.merchant_id,
                    event_id: data.item.event_id,
                    sort_by: 'name',
                    order_by: 'asc'
                  },
                }"
                >
                  參與會員紀錄
                </b-dropdown-item>
              </b-dropdown>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.REWARD_MISSION_LIST_VIEW])"
                variant="inverse-info"
                :to="{
                  name: 'RewardMissionList',
                  params: { reward_card_id: data.item.id },
                }"
              >
                <span class="mdi mdi-paw"></span> 任務
              </b-button>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.REWARD_GIFT_LIST_VIEW])"
                variant="inverse-success"
                :to="{
                  name: 'RewardGiftList',
                  params: { reward_card_id: data.item.id },
                }"
              >
                <span class="mdi mdi-gift"></span> 獎項
              </b-button>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.REWARD_CARD_LIST_VIEW])"
                variant="inverse-primary"
                :to="{
                  name: 'RewardCardView',
                  params: { reward_card_id: data.item.id },
                }"
              >
                <span class="mdi mdi-eye"></span> 查看
              </b-button>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.REWARD_CARD_MODIFY])"
                variant="inverse-warning"
                :to="{
                  name: 'RewardCardEdit',
                  params: { reward_card_id: data.item.id },
                }"
              >
                <span class="mdi mdi-lead-pencil"></span> 編輯
              </b-button>
              <b-button
                class="mr-1"
                v-if="checkPermission([consts.REWARD_CARD_DELETE])"
                variant="inverse-danger"
                @click="deleteRewardCard(data.item)"
              >
                <span class="mdi mdi-delete"></span> 刪除
              </b-button>
            </template>
          </b-table>
        </b-overlay>
        <CustomPagination
          :currentPage.sync="query.page"
          :totalRows="totalRows"
          :perPage.sync="query.per_page"
        />
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { format } from "date-fns";
import rewardCardApi from "@/apis/reward-card";
import { mapGetters, mapState } from "vuex";
import CopyUrlButton from "@/components/Dashboard/CopyUrlButton.vue";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import generateLiffUrl from "@/mixins/Dashboard/liffUrlHelper";
import sweetAlertPresets from "@/mixins/Dashboard/sweetAlertPresets";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import VueQr from "vue-qr";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {
    AppSingleSelect,
    CopyUrlButton,
    CustomPagination,
    VueQr,
    BreadCrumb
  },
  mixins: [generateLiffUrl, sweetAlertPresets, paginationMixin],
  data() {
    return {
      consts: consts,
      showLoading: true,
      rewardCards: [],
      fields: [
        { key: "title", label: "集點卡名稱", sortable: true },
        { key: "branch_id", label: "分店" },
        { key: "start_end_time", label: "使用期間" },
        { key: "point_number", label: "卡面點數" },
        { key: "actions", label: "管理" },
      ],
      totalRows: 0,
      initialized: false,
      query: {
        per_page: 10,
        page: 1,
        sort_by: "created_at",
        order_by: "desc",
        branch_id: null,
        keyword: "",
      },
      disabled_branch_select: true,
      selected_branch: {},
    };
  },
  computed: {
    ...mapGetters("general", ["myBranches", "currentBranch"]),
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    branchOptions() {
      let options = [];

      if (this.checkPermission([consts.REWARD_CARD_LIST_NO_BRANCH])) {
        options = [
          {
            value: "null",
            text: "品牌組織階層(無隸屬分店)",
          },
        ];
      }

      if (
        this.checkPermission([consts.REWARD_CARD_LIST_BRANCH]) ||
        options.length === 0
      ) {
        const option1 = this.myBranches.map((branch) => {
          let branchName = `${branch.branch_code} ${branch.name}`;
          return {
            value: branch.id,
            text: branchName,
          };
        });

        options = [
          ...options,
          ...option1, // ← 改這裡，把陣列展開
        ];
      }

      return options;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route() {
      if (!this.initialized) return;
      this.fetchRewardCards();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    // 有權限的話，可以編輯下拉
    if (this.checkPermission([consts.REWARD_CARD_LIST_BRANCH])) {
      this.disabled_branch_select = false;
    }

    // 預設為 現在的分店id
    if (this.query.branch_id != null) {
      this.selected_branch = this.branchOptions.find(
        (option) => option.value === this.query.branch_id
      );
    } else if (this.checkPermission([consts.REWARD_CARD_LIST_NO_BRANCH])) {
      this.selected_branch = this.branchOptions.find(
        (option) => option.value === "null"
      );
    }

    await this.fetchRewardCards();
    this.initialized = true;
    this.showLoading = false;
  },
  methods: {
    branchChange() {
      this.query.branch_id = this.selected_branch?.value ?? null;

      this.handlePageReset();
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async fetchRewardCards() {
      this.showLoading = true;
      try {
        if (this.query.branch_id === "null") {
          this.query.branch_id = null;
        }

        // 如果沒有branch_id 但是沒有無分店權限則自動帶入預設分店
        if (
          this.query.branch_id === null &&
          !this.checkPermission([consts.REWARD_CARD_LIST_NO_BRANCH])
        ) {
          this.query.branch_id = this.currentBranch.id;
          this.selected_branch = this.branchOptions.find(
            (option) => option.value === this.currentBranch.id
          );
        }

        const params = { ...this.query };
        const response = await rewardCardApi.list(params);
        this.rewardCards = response.data.data;
        this.totalRows = response.data.meta.total;

        this.showLoading = false;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取集點卡列表失敗", "error");

        this.showLoading = false;
      }
    },
    async deleteRewardCard(rewardCard) {
      this.showLoading = true;
      try {
        if (
          false ===
          (await this.confirmDeletion({
            text: `集點卡名稱：${rewardCard.title}`,
          }))
        ) {
          return;
        }

        await rewardCardApi.remove(rewardCard.id);
        this.$swal("刪除成功", "", "success");
        await this.fetchRewardCards();
      } catch (error) {
        console.error(error);
        this.$swal("刪除失敗", "", "error");
      } finally {
        this.showLoading = false;
      }
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageReset();
    },
    handlePageReset() {
      const currentPage = parseInt(this.query.page, 10);

      if (currentPage === 1) {
        updateUrl(this.query, this.$store, this.$router);
      } else {
        this.query.page = 1;
      }
    },
    dateFormatter: (value) => {
      if (!value) return "無";
      return format(new Date(value), "yyyy-MM-dd");
    },
    downloadQRCode(filename) {
      const base64QRCodeImage = this.$refs?.qrcode?.$el?.src;
      if (base64QRCodeImage) {
        const temporaryDownloadLink = document.createElement("a");
        temporaryDownloadLink.href = base64QRCodeImage;
        temporaryDownloadLink.download = filename
          ? `${filename}.png`
          : "qrcode.png";
        temporaryDownloadLink.style.display = "none";
        document.body.appendChild(temporaryDownloadLink);
        temporaryDownloadLink.click();
      }
    },
    getBranchName(branch_id) {
      const option = this.branchOptions.find((option) =>
        branch_id === null
          ? option.value === "null"
          : option.value === branch_id
      );

      return option ? option.text : "";
    },
  },
};
</script>
